import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DurationSummary } from '../hooks/useOverviewData';

interface Props {
    data: DurationSummary | null;
    previous?: DurationSummary | null;
    loading: boolean;
}

const DAY_SECONDS = 86_400;
const HOUR_SECONDS = 3_600;

/**
 * End-to-end lead time card. Shows the median (p50) as the hero number,
 * p95 as the "long tail" secondary, sample size for credibility, and a
 * delta vs previous period on the median when comparison is on.
 *
 * Cohort = agreements whose last completion fell in the window (so the
 * number answers "for what we delivered this period, how long did it
 * take?", not "for what we just signed, how slow are we").
 */
const LeadTimeCard: React.FC<Props> = ({ data, previous, loading }) => {
    const intl = useIntl();

    const deltaPct = useMemo(() => {
        if (!data || !previous || data.p50 == null || previous.p50 == null || previous.p50 <= 0) {
            return null;
        }
        return Math.round(((data.p50 - previous.p50) / previous.p50) * 1000) / 10;
    }, [data, previous]);

    const isEmpty = data != null && data.sample_size === 0;

    return (
        <section className="av2-card av2-leadtime">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">
                        <FormattedMessage
                            id="analyticsV2.leadTime.title"
                            defaultMessage="End-to-end lead time"
                        />
                    </h3>
                    <p className="av2-card__subtitle">
                        <FormattedMessage
                            id="analyticsV2.leadTime.subtitle"
                            defaultMessage="Agreement signed → last completion, for projects finishing in this period"
                        />
                    </p>
                </div>
            </div>
            <div className="av2-card__body">
                {loading || !data ? (
                    <LeadTimeSkeleton />
                ) : isEmpty ? (
                    <div className="av2-empty">
                        <FormattedMessage
                            id="analyticsV2.leadTime.empty"
                            defaultMessage="No projects finished in this period yet."
                        />
                    </div>
                ) : (
                    <div className="av2-leadtime__body">
                        <div className="av2-leadtime__hero">
                            <span className="av2-leadtime__label">
                                <FormattedMessage
                                    id="analyticsV2.leadTime.median"
                                    defaultMessage="Typical"
                                />
                            </span>
                            <span className="av2-leadtime__value">
                                {data.p50 != null ? formatDuration(data.p50, intl) : '—'}
                            </span>
                            {deltaPct != null && Math.abs(deltaPct) >= 0.5 && (
                                <span
                                    className={`av2-leadtime__delta av2-leadtime__delta--${
                                        // lower is better — flip sign for color
                                        deltaPct < 0 ? 'up' : 'down'
                                    }`}
                                >
                                    {deltaPct < 0 ? '↓' : '↑'} {Math.abs(deltaPct).toFixed(0)}%
                                    {' '}
                                    <FormattedMessage
                                        id="analyticsV2.leadTime.vsLast"
                                        defaultMessage="vs last period"
                                    />
                                </span>
                            )}
                        </div>
                        <div className="av2-leadtime__secondary">
                            <div className="av2-leadtime__metric">
                                <span className="av2-leadtime__metric-label">
                                    <FormattedMessage
                                        id="analyticsV2.leadTime.p95"
                                        defaultMessage="Slowest 5%"
                                    />
                                </span>
                                <span className="av2-leadtime__metric-value">
                                    {data.p95 != null ? formatDuration(data.p95, intl) : '—'}
                                </span>
                            </div>
                            <div className="av2-leadtime__metric">
                                <span className="av2-leadtime__metric-label">
                                    <FormattedMessage
                                        id="analyticsV2.leadTime.fastest"
                                        defaultMessage="Fastest"
                                    />
                                </span>
                                <span className="av2-leadtime__metric-value">
                                    {data.min != null ? formatDuration(data.min, intl) : '—'}
                                </span>
                            </div>
                            <div className="av2-leadtime__metric">
                                <span className="av2-leadtime__metric-label">
                                    <FormattedMessage
                                        id="analyticsV2.leadTime.sample"
                                        defaultMessage="Based on"
                                    />
                                </span>
                                <span className="av2-leadtime__metric-value">
                                    <FormattedMessage
                                        id="analyticsV2.leadTime.sampleCount"
                                        defaultMessage="{count, plural, one {# project} other {# projects}}"
                                        values={{ count: data.sample_size }}
                                    />
                                </span>
                            </div>
                        </div>
                        {data.p25 != null && data.p75 != null && data.p50 != null && data.max != null && (
                            <DistributionBar
                                p25={data.p25}
                                p50={data.p50}
                                p75={data.p75}
                                min={data.min ?? 0}
                                max={data.max}
                            />
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

interface DistributionBarProps {
    p25: number;
    p50: number;
    p75: number;
    min: number;
    max: number;
}

/**
 * Compact "box plot lite": shows the IQR band [p25, p75] and a thin marker
 * at p50, all scaled to [min, max]. Tells the eye whether the lead-time
 * distribution is tight or scattered without a full chart.
 */
const DistributionBar: React.FC<DistributionBarProps> = ({ p25, p50, p75, min, max }) => {
    const range = max - min || 1;
    const left = ((p25 - min) / range) * 100;
    const width = ((p75 - p25) / range) * 100;
    const markerLeft = ((p50 - min) / range) * 100;

    return (
        <div className="av2-leadtime__dist" aria-hidden="true">
            <div className="av2-leadtime__dist-track">
                <div
                    className="av2-leadtime__dist-iqr"
                    style={{ left: `${left}%`, width: `${Math.max(width, 1)}%` }}
                />
                <div
                    className="av2-leadtime__dist-marker"
                    style={{ left: `${markerLeft}%` }}
                />
            </div>
        </div>
    );
};

const LeadTimeSkeleton: React.FC = () => (
    <div className="av2-leadtime__body">
        <div className="av2-leadtime__hero">
            <div className="av2-skel av2-skel--leadtime-label" />
            <div className="av2-skel av2-skel--leadtime-value" />
        </div>
        <div className="av2-leadtime__secondary">
            {[0, 1, 2].map((i) => (
                <div key={i} className="av2-leadtime__metric">
                    <div className="av2-skel av2-skel--leadtime-metric-label" />
                    <div className="av2-skel av2-skel--leadtime-metric-value" />
                </div>
            ))}
        </div>
    </div>
);

const formatDuration = (
    seconds: number,
    intl: { formatMessage: (msg: { id: string; defaultMessage?: string }, values?: Record<string, unknown>) => string },
): string => {
    if (seconds >= DAY_SECONDS) {
        const days = Math.round((seconds / DAY_SECONDS) * 10) / 10;
        return intl.formatMessage(
            {
                id: 'analyticsV2.leadTime.daysShort',
                defaultMessage: '{days, plural, one {# day} other {# days}}',
            },
            { days },
        );
    }
    if (seconds >= HOUR_SECONDS) {
        const hours = Math.round((seconds / HOUR_SECONDS) * 10) / 10;
        return intl.formatMessage(
            {
                id: 'analyticsV2.leadTime.hoursShort',
                defaultMessage: '{hours, plural, one {# hour} other {# hours}}',
            },
            { hours },
        );
    }
    const minutes = Math.max(1, Math.round(seconds / 60));
    return intl.formatMessage(
        {
            id: 'analyticsV2.leadTime.minutesShort',
            defaultMessage: '{minutes, plural, one {# minute} other {# minutes}}',
        },
        { minutes },
    );
};

export default LeadTimeCard;
